<script setup lang="ts">
import { computed, ref, toRaw, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import RoundingMode = App.Data.RuleEngine.Rules.Rounding.RoundingMode;
import OutputVariableDTO = App.Data.RuleEngine.OutputVariableDTO;

const { t } = useI18n();
const store = useRuleEnginedStore();

const isDialogVisible = defineModel<boolean>();

const roundingModes: { label: string; value: RoundingMode }[] = [
	{ label: "None", value: "none" },
	{ label: "Regular (Half Up)", value: "half_up" },
	{ label: "Up (Ceil)", value: "ceil" },
	{ label: "Down (Floor)", value: "floor" },
];

const resolutionOptions = [
	{ label: "5 cents (0.05)", value: 0.05 },
	{ label: "10 cents (0.10)", value: 0.1 },
	{ label: "25 cents (0.25)", value: 0.25 },
	{ label: "50 cents (0.50)", value: 0.5 },
	{ label: "1 euro (1.00)", value: 1.0 },
	{ label: "5 euros (5.00)", value: 5.0 },
	{ label: "10 euros (10.00)", value: 10.0 },
];

/**
 * Variables belong to the version type currently being edited. This used to be
 * hardcoded to "per_hour", which throws on models that do not define it.
 */
const versionType = computed(
	() => store.ruleEngineState.selectedModelVersionType
);

const sourceVariables = computed(
	(): OutputVariableDTO[] =>
		store.activeRuleModelVersion?.output_variables?.[versionType.value] ?? []
);

/** Edited against a copy, so closing without applying changes nothing. */
const variables = ref<OutputVariableDTO[]>([]);

const initializeVariables = () => {
	variables.value = sourceVariables.value
		.filter((variable) => variable.type === "float")
		.map((variable) => structuredClone(toRaw(variable)));
};

watch(sourceVariables, initializeVariables, { immediate: true });

const applyChanges = () => {
	const target = store.activeRuleModelVersion?.output_variables?.[versionType.value];
	if (!target) return;

	variables.value.forEach((variable) => {
		const index = target.findIndex(
			(candidate) => candidate.name === variable.name
		);
		if (index !== -1) target.splice(index, 1, variable);
	});

	isDialogVisible.value = false;
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" persistent width="70vw" scrollable>
		<v-card elevation="9">
			<v-toolbar>
				<v-toolbar-title>Rounding and limits</v-toolbar-title>
				<v-spacer />
				<v-btn icon @click="isDialogVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>

			<v-divider />

			<v-card-text class="pa-5">
				<p class="intro mb-5">
					Applied to the values this model produces for
					<strong>{{ t(`rule_engine.versionTypes.${versionType}.label`) }}</strong
					>, after all rules have run.
				</p>

				<div v-if="variables.length === 0" class="empty">
					<i>This model has no adjustable output variables.</i>
				</div>

				<div
					v-for="(config, index) in variables"
					:key="config.name"
					class="variable"
				>
					<div class="font-weight-medium mb-2">
						{{ t(`rule_engine.action_variables.${config.name}`) }}
					</div>

					<v-row dense>
						<v-col cols="12" md="3">
							<v-select
								:id="`mode${index}`"
								label="Rounding"
								v-model="config.roundingMode"
								:items="roundingModes"
								item-title="label"
								item-value="value"
								variant="filled"
								density="comfortable"
								hide-details
							/>
						</v-col>
						<v-col cols="12" md="3" v-if="config.roundingMode !== 'none'">
							<v-select
								:id="`resolution${index}`"
								label="Round to"
								v-model="config.roundingResolution"
								:items="resolutionOptions"
								item-title="label"
								item-value="value"
								variant="filled"
								density="comfortable"
								hide-details
							/>
						</v-col>
						<v-col cols="6" md="3">
							<v-text-field
								:id="`min${index}`"
								label="Minimum"
								v-model="config.min"
								variant="filled"
								density="comfortable"
								type="number"
								step="0.01"
								hide-details
							/>
						</v-col>
						<v-col cols="6" md="3">
							<v-text-field
								:id="`max${index}`"
								label="Maximum"
								v-model="config.max"
								variant="filled"
								density="comfortable"
								type="number"
								step="0.01"
								hide-details
							/>
						</v-col>
					</v-row>
				</div>
			</v-card-text>

			<v-divider />

			<v-card-actions class="pa-3">
				<v-spacer />
				<v-btn variant="text" color="error" @click="isDialogVisible = false">
					Cancel
				</v-btn>
				<v-btn
					class="ml-5"
					variant="tonal"
					color="primary"
					:disabled="variables.length === 0"
					@click="applyChanges"
				>
					Apply
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
.intro {
	font-size: 0.875rem;
	opacity: 0.8;
}

.variable {
	padding: 0.75rem 0;

	& + & {
		border-top: 1px solid rgba(0, 0, 0, 0.08);
	}
}

.empty {
	opacity: 0.75;
}
</style>
